import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";
import { Box, Button } from "@mui/material";
import { useGetFindings, useExportFindings } from "../../../hooks/findings";
import { Finding } from "../../../types/findings";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const UserFindingTable = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState<string | undefined>("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [columnFilters]);

  const {
    data: findingQueryResults = [],
    isError: isLoadingFindingsError,
    isFetching: isFetchingFindings,
    isLoading: isLoadingFindings,
  } = useGetFindings({ pagination, columnFilters, sorting, globalFilter });

  const fetchedFindings = findingQueryResults[0]?.findings || [];

  useEffect(() => {
    const count = findingQueryResults[0]?.rowCount;
    if (count == 0) {
      setRowCount(0);
    }
    if (!count || count === rowCount) {
      return;
    }
    setRowCount(count);
  }, [findingQueryResults]);

  const exportFindings = useExportFindings(columnFilters, sorting);

  const handleExport = async () => {
    try {
      await exportFindings();
    } catch (error) {
      toast.error("Failed to export");
    } finally {
    }
  };

  const tableName = "findings_table";
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const tableState = JSON.parse(localStorage.getItem("tableState") || "{}");
    return (
      tableState[tableName]?.columnVisibility || {
        id: false,
      }
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "tableState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("tableState") || "{}"),
        [tableName]: {
          columnVisibility,
        },
      })
    );
  }, [columnVisibility, tableName]);

  const columns = useMemo<MRT_ColumnDef<Finding>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },

      {
        accessorKey: "name",
        header: "Name",
        size: 220,
        enableEditing: (row) => !row.original.id,
        Cell: ({ row }) => {
          return (
            <>
              <Link to={`/finding/${row.original.event_id}`}>
                {row.original.name}
              </Link>
            </>
          );
        },
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              name: undefined,
            }),
        },
      },

      {
        accessorKey: "event_time",
        header: "Time",
        size: 140,
        enableColumnFilter: true,
        enableSorting: true,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "notes",
        header: "Notes",
        size: 320,
        enableSorting: false,
        enableColumnFilter: false,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "details",
        header: "Details",
        size: 820,
        Cell: ({ row }) => (
          <div style={{ textAlign: "left", maxHeight: "310px" }}>
            <ReactMarkdown>{row.original.details}</ReactMarkdown>
          </div>
        ),
        enableSorting: false,
        enableEditing: false,
        enableColumnFilter: false,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "assignee",
        header: "Analyst",
        size: 140,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "risk",
        header: "Risk",
        size: 40,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 60,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "hostname",
        header: "Hostname",
        size: 140,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "event_id",
        header: "EventId",
        size: 30,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
    ],
    [validationErrors]
  );

  const table = useMaterialReactTable({
    columns,
    enableGlobalFilter: false,
    data: fetchedFindings,
    initialState: {
      showColumnFilters: true,
      density: "compact",
      columnVisibility: columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    selectAllMode: "all",
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    rowNumberDisplayMode: "original",
    muiToolbarAlertBannerProps: isLoadingFindingsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button variant="contained" onClick={handleExport}>
          Export {rowCount} Rows
        </Button>
      </Box>
    ),

    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility: columnVisibility,
      isLoading: isLoadingFindings,
      showAlertBanner: isLoadingFindingsError,
      showProgressBars: isFetchingFindings,
    },
  });

  return <MaterialReactTable table={table} />;
};

export default UserFindingTable;
